/**
 * Plant Finder likelihood tiers, shown on a collection that was sent from CSP.
 *
 * A collection created from a Plant Finder list holds the species; the tier each
 * one earned (confirmed / nearby / possible) lives in the linked snapshot on the
 * backend, fetched from GET /api/plant-finder-lists/by-collection/:collectionId.
 * That endpoint 404s for any ordinary collection, so everything here renders
 * nothing unless the collection actually came from Plant Finder.
 *
 * A tier is a property of (species x drawn area x specimen scrape), not of the
 * species — which is why it is fetched per collection and never read from the
 * plant record, and why the banner always shows the buffer and scrape the tiers
 * were computed against. "Possible" on its own is a weaker, vaguer claim than
 * "possible, ±15 km, 2026-Q3"; the context is what makes it legible.
 */
import React, { useEffect, useState } from "react";
import { Tag, Tooltip } from "antd";
import Api from "api";

export type Tier = "confirmed" | "nearby" | "possible";

export interface TierEntry {
  slp_id: number;
  tier: Tier;
  in_count: number | null;
  nearest_km: number | null;
}

export interface PlantFinderTierData {
  list_id: string;
  name?: string;
  sent_at?: string | null;
  received_at?: string;
  context: {
    survey_effort?: number;
    buffer_km?: number;
    gbif_scrape?: string;
    candidate_count?: number;
  };
  counts: Record<Tier, number>;
  /** Keyed by plant _id — the same id a collection row carries as `original_id`. */
  tiers: Record<string, TierEntry>;
}

export const TIER_COLUMN_KEY = "plant_finder_tier";

const TIER_META: Record<Tier, { label: string; color: string; rank: number }> = {
  confirmed: { label: "Confirmed", color: "green", rank: 0 },
  nearby: { label: "Nearby", color: "gold", rank: 1 },
  possible: { label: "Possible", color: "default", rank: 2 },
};

/**
 * Fetch the tiers for a collection. Resolves to null for any collection that did
 * not come from Plant Finder (the endpoint's 404) and on any error — a missing
 * tier column must never break the collection page.
 */
export function usePlantFinderTiers(collectionId?: string | null) {
  const [data, setData] = useState<PlantFinderTierData | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    if (!collectionId) return;
    (async () => {
      try {
        const res: any = await Api.get(`/plant-finder-lists/by-collection/${collectionId}`, {});
        const body = res && res.data ? res.data : null;
        if (!cancelled && body && body.tiers) setData(body);
      } catch (e) {
        // 404 is the normal answer for an ordinary collection. Stay silent.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collectionId]);

  return data;
}

function tierTooltip(e: TierEntry, ctx: PlantFinderTierData["context"]): string {
  const scrape = ctx.gbif_scrape ? ` (${ctx.gbif_scrape} scrape)` : "";
  if (e.tier === "confirmed") {
    const n = e.in_count;
    return n != null
      ? `Herbarium specimen inside the drawn area — ${n} specimen cell${n === 1 ? "" : "s"}${scrape}`
      : `Herbarium specimen inside the drawn area${scrape}`;
  }
  if (e.tier === "nearby") {
    const km = e.nearest_km != null ? `~${Math.round(e.nearest_km * 10) / 10} km away` : "near the area";
    const buf = ctx.buffer_km != null ? `, within the ${ctx.buffer_km} km buffer` : "";
    return `No specimen inside the area; nearest ${km}${buf}${scrape}`;
  }
  return `Jepson range allows it here, but no specimen inside the area or its${
    ctx.buffer_km != null ? ` ${ctx.buffer_km} km` : ""
  } buffer${scrape}`;
}

/**
 * A table column showing each row's tier. Rows are matched on `original_id`, the
 * plant _id the collection API puts on every row.
 */
export function buildTierColumn(data: PlantFinderTierData, sortOrder: any = null) {
  const tierOf = (record: any): TierEntry | undefined =>
    record && record.original_id ? data.tiers[String(record.original_id)] : undefined;

  return {
    title: "Likelihood",
    key: TIER_COLUMN_KEY,
    dataIndex: TIER_COLUMN_KEY,
    width: 130,
    show: true,
    sortOrder,
    // Confirmed first, then nearby, then possible; rows with no tier last.
    sorter: (a: any, b: any) => {
      const ra = tierOf(a) ? TIER_META[tierOf(a)!.tier].rank : 9;
      const rb = tierOf(b) ? TIER_META[tierOf(b)!.tier].rank : 9;
      return ra - rb;
    },
    render: (_: any, record: any) => {
      const e = tierOf(record);
      if (!e) return null;
      const meta = TIER_META[e.tier];
      return (
        <Tooltip title={tierTooltip(e, data.context)}>
          <Tag color={meta.color}>{meta.label}</Tag>
        </Tooltip>
      );
    },
  };
}

/** Summary and the parameters the tiers were computed against. */
export function PlantFinderContextBanner({ data }: { data: PlantFinderTierData }) {
  const { counts, context } = data;
  const params: string[] = [];
  if (context.gbif_scrape) params.push(`GBIF herbarium specimens, ${context.gbif_scrape} scrape`);
  if (context.buffer_km != null) params.push(`±${context.buffer_km} km buffer`);
  if (context.survey_effort != null) {
    params.push(`${context.survey_effort.toLocaleString()} specimens inside the area`);
  }

  return (
    <div
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        padding: "8px 12px",
        margin: "0 0 12px",
        background: "#fafafa",
        lineHeight: 1.6,
      }}
    >
      <div>
        <strong>Plant Finder list</strong>
        {"  "}
        <Tag color={TIER_META.confirmed.color}>{counts.confirmed} confirmed</Tag>
        <Tag color={TIER_META.nearby.color}>{counts.nearby} nearby</Tag>
        <Tag color={TIER_META.possible.color}>{counts.possible} possible</Tag>
      </div>
      {params.length > 0 && (
        <div style={{ color: "#595959", fontSize: 13 }}>
          Tiers computed against {params.join(" · ")}. A tier describes this drawn area
          at this scrape — the same species can rate differently elsewhere.
        </div>
      )}
    </div>
  );
}
